import { NEXT_PUBLIC_BASE_URL } from '@/constants/constants';
import { BreadcrumbItem } from '@/types/services';

export const CONTENTSET_TITLE = 'スクーミーAPIの使い方';
export const BASE_URL = '/hello-schoomy/api-guide/s1';

export type codeBlockOverViewItem = {
    blockName: string;
    description: string;
    detail_1: string;
    detail_2?: string;
    codeBlockType: string;
    isBlueboard: boolean;
    isOrangeboard: boolean;
};

export type useCaseItem = {
    title: string;
    description?: string;
    steps: stepItem[];
};

export type stepItem = {
    stepNumber?: number;
    stepTitle?: string;
    examples: exampleItem[];
    button?: ButtonItem;
};

export type exampleItem = {
    description?: string;
    imageDescription?: string;
    images?: imageItem[];
};

export type imageItem = {
    imageName: string;
    baseWidth?: number;
    lgWidth?: number;
};

export type ButtonItem = {
    title: string;
    titleColor?: string;
    backgroundColor?: string;
    url: string;
};

export type blueBoardGuideDataItem = {
    contentsCd: string;
    connectorType: ConnectorType;
    codeBlockOverView: codeBlockOverViewItem;
    useCases: useCaseItem[];
};

export type ConnectorType =
    | 'wifi'
    | 'wifiRouter'
    | 'wifiClient'
    | 'sheetWrite'
    | 'sheetRead'
    | 'timer'
    | 'webSwitch'
    | 'webMonitor'
    | 'slack'
    | 'weatherGeo'
    | 'weatherCity'
    | 'lineMessaging';

export const contentCdMap: Record<ConnectorType, string> = {
    wifi: '01',
    wifiRouter: '02',
    wifiClient: '03',
    sheetWrite: '04',
    sheetRead: '05',
    timer: '06',
    webSwitch: '07',
    webMonitor: '08',
    slack: '09',
    weatherGeo: '10',
    weatherCity: '11',
    lineMessaging: '12',
};

export const categoryBlockType: Record<string, string> = {
    control: 'コントロール',
    action: 'アクション',
    sensing: 'センシング',
    switch: 'スイッチ',
    ir_communication: '赤外線通信',
    gps: 'GPS',
    file: 'ファイル',
    area: 'エリア',
    data: 'データ',
    calculation: '計算',
    number: '数',
    custom_block: '自作ブロック・関数',
    network: 'ネットワーク',
    api: 'API',
    display: '表示する',
};

export const BLUE_BOARD_GUIDE_DATA: readonly blueBoardGuideDataItem[] = [
    {
        contentsCd: '01',
        connectorType: 'wifi',
        codeBlockOverView: {
            blockName: 'Wi-Fiにつなぐブロック',
            description: 'ブルーボードを Wi-Fi につなぐプログラムです。',
            detail_1: 'Wi-Fi の接続設定を行うことできます。',
            codeBlockType: categoryBlockType.network,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'APIブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: `API ブロックを使用するには利用者登録が必要になります。
                                    Wi-Fi につなぐブロックのメールアドレス入力欄には利用者登録時のメールアドレスを入力します。`,
                            },
                        ],
                    },
                ],
            },
            {
                title: 'Wi-Fi につなぐブロックを配置する',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックを、 ずっとブロックにつないでください',
                        examples: [
                            {
                                description: 'ネットワークの中にあります。',
                                images: [
                                    {
                                        imageName: '545909d4.png',
                                        baseWidth: 260,
                                        lgWidth: 450,
                                    },
                                    {
                                        imageName: '3d0e73b4.png',
                                        baseWidth: 260,
                                        lgWidth: 450,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'Wi-FiにつなぐブロックのSSID、 パスワード、 メールアドレスを入力してください',
                        examples: [
                            {
                                description: `①SSIDとパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    　SSID は、 2.4GHzのものを選択してください。`,
                                images: [
                                    {
                                        imageName: '221b6b10.png',
                                        baseWidth: 260,
                                        lgWidth: 480,
                                    },
                                ],
                            },
                            {
                                description: `②Wi-Fi につなぐブロックにSSIDとパスワードとメールアドレスを入力してください。
                                    　※2回目以降は、 SSID、 パスワード、 メールアドレスの入力は不要です。`,
                                images: [
                                    {
                                        imageName: '608cbcc0.png',
                                        baseWidth: 260,
                                        lgWidth: 480,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'Wi-Fi に接続できるか確認する',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'ブルーボードにプログラムをアップロードしてください',
                        examples: [
                            {
                                description: 'スクーミーボードを PC に接続し、 ルータの近くで行って下さい。',
                                images: [
                                    {
                                        imageName: '8b81049d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'シリアルモニターを開いて確認してください',
                        examples: [
                            {
                                description: 'Connected to WiFi. と表示されれば Wi-Fi の接続完了です！',
                                images: [
                                    {
                                        imageName: 'e51e6fbd.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 3,
                        stepTitle: 'Wi-Fi の接続状況を確認してください',
                        examples: [
                            {
                                description:
                                    '①接続確認が【ON】になっている場合に、 左下にLED を差しこむと Wi-Fi 接続状況を確認する事が出来ます。',
                                images: [
                                    {
                                        imageName: '836b5073.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                            {
                                description:
                                    '②Wi-Fi に接続されていれば左下の LED が光り、Wi-Fi の接続が切れると LED が消えます。',
                                imageDescription: `※左下で別のコネクタを使いたい場合は、 接続確認を【OFF】に変更して書き込みを行って下さい。
                                    ※Wi-Fi 接続状況を確認出来るのは左下のみとなります。`,
                                images: [
                                    {
                                        imageName: '936eb69d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '02',
        connectorType: 'wifiRouter',
        codeBlockOverView: {
            blockName: 'Wi-Fi 親機になるブロック と Wi-Fi 子機になるブロック（送信する条件あり）',
            description: '2 台のブルーボードを接続するプログラムです。',
            detail_1: '2 台のブルーボードを親機と子機として接続できます。',
            detail_2: '条件を指定して、 子機のボードからデータを送信することができます。',
            codeBlockType: categoryBlockType.network,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: '部屋の電気がついているとき LED が点灯するようにしよう',
                description: `このプログラムでは、 子機に接続された明るさセンサーが 50 以上の値を取得したときに LED が点滅します。
                    下は親機となるブルーボードにアップロードするプログラムです。`,
                steps: [
                    {
                        examples: [
                            {
                                imageDescription: '親機となるブルーボードにアップロードするプログラムです。',
                                images: [
                                    {
                                        imageName: 'c15c0ad9.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                            {
                                imageDescription: '子機となるブルーボードにアップロードするプログラムです。',
                                images: [
                                    {
                                        imageName: '7dabe54d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '親機と子機が近くにいると LED が点滅するようにしよう',
                description: `ずっと送信するブロックを使用したプログラムです。
                    ずっと送信するブロックを使用することで、条件なく親機にデータを送信することができます。
                    このプログラムでは、 子機と親機が送受信可能な距離にあると LED が点滅します。`,
                steps: [
                    {
                        examples: [
                            {
                                imageDescription: '親機となるブルーボードにアップロードするプログラムです。',
                                images: [
                                    {
                                        imageName: 'cf885d58.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                            {
                                imageDescription: '子機となるブルーボードにアップロードするプログラムです。',
                                images: [
                                    {
                                        imageName: '2e757217.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '03',
        connectorType: 'wifiClient',
        codeBlockOverView: {
            blockName: 'Wi-Fi 親機になるブロック と Wi-Fi 子機になるブロック（送信する条件なし）',
            description: '2 台のブルーボードを接続するプログラムです。',
            detail_1: '2 台のブルーボードを親機と子機として接続できます。',
            detail_2: '条件を指定せずに、 子機のボードからデータを送信することができます。',
            codeBlockType: categoryBlockType.network,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: '子機のある部屋が暗いとき、 親機に接続された LED が点滅するようにしよう',
                description: `このプログラムでは、 子機に接続された明るさセンサーの値を親機が受け取り、 変数 data に格納しています。
                    また、変数 data が 30 以下のとき LED が点滅します。`,
                steps: [
                    {
                        examples: [
                            {
                                imageDescription: '親機となるブルーボードにアップロードするプログラムです。',
                                images: [
                                    {
                                        imageName: 'b0158703.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                            {
                                imageDescription: '子機となるブルーボードにアップロードするプログラムです。',
                                images: [
                                    {
                                        imageName: 'de8d0e9e.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '04',
        connectorType: 'sheetWrite',
        codeBlockOverView: {
            blockName: 'スプレッドシート書き込みブロック',
            description: 'センサーが取得した数値をスプレッドシートに書き込むプログラムです。',
            detail_1: 'センサーが取得した数値をスプレッドシートに書き込むプログラムです。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'スプレッドシート書き込みブロックの使い方',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: '72535113.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: ' Wi-Fi につなぐブロックの下にスプレッドシートブロックをつなげてください',
                        examples: [
                            {
                                imageDescription: `以上でスプレッドシートブロックを使用する準備は完了です。
                                    プログラムをブルーボードに書き込むと、 入力いただいたメールアドレスへスプレッドシートのリンクが送信されます。
                                    また、 シリアルモニターよりスプレッドシートのリンクを入手することができます。                                    
                                    いずれかの方法でスプレッドシートへアクセスしてください。`,
                                images: [
                                    {
                                        imageName: 'fdd3d396.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '部屋の明るさ、 温度をスプレッドシートに記録しよう',
                description: `下のプログラムでは、 1 分間隔でスプレッドシートへ明るさセンサー、 温度センサーの取得した値が記録されます。
                    ヘッダラベルをそれぞれ「明るさ」、「温度」とすることで、 スプレッドシートで明るさセンサーから取得した値の表頭を「明るさ」、 温度センサーから取得した値の表頭を「温度」と変更することができます。`,
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'ヘッダラベルを設定してください',
                        examples: [
                            {
                                description: 'ヘッダラベルを「明るさ」、「温度」に変更します。',
                                images: [
                                    {
                                        imageName: '0264dd38.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'シリアルモニターを開いて確認してください',
                        examples: [
                            {
                                description: '実行後、 シリアルモニターにシートのリンクが表示されます。',
                                images: [
                                    {
                                        imageName: 'c74f0347.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 3,
                        stepTitle: 'スプレッドシートの記録を確認してください',
                        examples: [
                            {
                                description:
                                    'リンクをコピーしてブラウザで検索すると、 明るさと温度が表示されたスプレッドシートが出てきます。',
                                images: [
                                    {
                                        imageName: 'c004ce9a.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '05',
        connectorType: 'sheetRead',
        codeBlockOverView: {
            blockName: 'スプレッドシート読み込みブロック',
            description: 'スプレッドシートのセルの数値を読み取るプログラムです。',
            detail_1: 'Google スプレッドシートの指定したセルから数値を読み取ることができます。',
            detail_2: '列と行を指定するか、 最新の行のセルから数値を読み取ることができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'スプレッドシート読み込みブロックの使い方',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: 'a42ca6b1.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle:
                            ' Wi-Fi につなぐブロックの下にスプレッドシート読み込みブロックをつなげて、シートIDに読み込みたいスプレッドシートのURLを入力してください',
                        examples: [
                            {
                                imageDescription: `上の画像では、 シート ID に入力した URL のスプレッドシートの 1 列目、 1 行目の値を変数 i に代入します。
                                    以上でスプレッドシート読み込みブロックを使用する準備は完了です。`,
                                images: [
                                    {
                                        imageName: '4596207c.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'スプレッドシートに書き込まれていた数を 7SEG に表示しよう',
                description: `シート ID に入力された URL のスプレッドシートより、 指定の列、 行から数を受け取るプログラムです。
                    受け取った値は変数 i に代入され、 変数 i を 7SEG で表示しています。`,
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'シート ID に URL を入力してください',
                        examples: [
                            {
                                description: `受け取った値は変数 i に代入され、 変数 i を 7SEG で表示しています。
                                    スプレッドシート読み込みブロックのシート ID には、 使用したいスプレッドシートの URL を入力してください。
                                    ※スプレッドシートのシート ID は保存されないため、 スプレッドシート読み込みブロックを使用するときは毎度入力してください。`,
                                images: [
                                    {
                                        imageName: 'd5a77062.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: '読み取り位置を指定してください',
                        examples: [
                            {
                                description: `上のプログラムでは 2 列 3 行で指定しています。
                                    2 列 3 行は、 実際のスプレッドシートで下のセルを指定しています。 列は A、 B、 C、 …で表示されますが、A が 1、 B が 2、 C が 3、 …というように対応しています。
                                    また、 列、 行の数を変更することで特定のセルから数を受け取ることができます。
                                    ※選択したセルから文字を受け取ることはできませんのでご注意ください`,
                                images: [
                                    {
                                        imageName: '440acd64.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'スプレッドシートの最新行を 7SEG に表示しよう',
                description: `読込行を最新行にすることで、 スプレッドシートの一番下の行を読み込むことができます。
                    上記の画像のスプレッドシートであれば、「24.8」が表示されます。
                    ※読込行が最新行であれば、 行の「3」は無視されます。`,
                steps: [
                    {
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'ac29f4c1.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '06',
        connectorType: 'timer',
        codeBlockOverView: {
            blockName: 'タイマーブロック',
            description: '時間を設定してブルーボードを動作させるプログラムです。',
            detail_1: '指定した日時や、 毎日同じ時間などにブルーボードを動作させることができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'タイマーブロックの使い方',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: 'e4e6dc50.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'Wi-Fi につなぐブロックの下にタイマーブロックをつなげてください',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'e0f9da88.png',
                                        baseWidth: 200,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '時間設定ブロックについて',
                steps: [
                    {
                        examples: [
                            {
                                description: `時間設定ブロックの各項目を設定することで、 プログラムを動かしたい時間を設定することができます。
                                    ※2 月 30 日など、 カレンダーに存在しない日付は設定できません。`,
                                images: [
                                    {
                                        imageName: '43ec58a9.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepTitle: '年を「毎年」に設定した場合',
                        examples: [
                            {
                                description:
                                    '毎年プログラムが動きます。 年を 2025 年に設定した場合、 2025 年にのみ動くようになります。',
                                images: [
                                    {
                                        imageName: '966c791b.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepTitle: '月を「毎月」に設定した場合',
                        examples: [
                            {
                                description:
                                    '毎月プログラムが動きます。 月を 5 月に設定した場合、5 月にのみ動くようになります。',
                                images: [
                                    {
                                        imageName: '320839f5.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepTitle: '日を「毎日」に設定した場合',
                        examples: [
                            {
                                description:
                                    '毎日プログラムが動きます。日を 10日に設定した場合、10 日にのみ動くようになります。',
                                images: [
                                    {
                                        imageName: 'bf4e77b1.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepTitle: '月の末日を指定したい場合',
                        examples: [
                            {
                                description:
                                    'その月の最終日を指定したいときは、 日付選択のいちばん下にある「末日」を選んでください。',
                                images: [
                                    {
                                        imageName: '2d065414.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '毎日登校時間になったら音が鳴るようにしよう',
                steps: [
                    {
                        examples: [
                            {
                                description: `タイマーブロックに < 設定 > にセットした時間になると、 < 時間がきたら > 内のプログラムが動作します。
                                    また、 < リセット > のエリアが正しいときタイマーブロックはリセットされ、 ふたたび < 設定 > にセットした時間になると < 時間がきたら > 内のプログラムが動作します。
                                    以下のプログラムでは、 7 時 30 分になると音コネクターから音が鳴ります。
                                    また、 タッチセンサーにふれることで、 次の日の 7 時 30 分にふたたび音を鳴らすことができます。`,
                                images: [
                                    {
                                        imageName: 'cf52dd50.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '特定の記念日になったら音が鳴るようにしよう',
                steps: [
                    {
                        examples: [
                            {
                                description: `下のプログラムでは、 タイマーブロックの < 設定 > に日付をセットしています。
                                    日付をセットした場合、< 時間がきたら > 内のプログラムが動作しているとき < リセット > のエリアが正しければタイマーブロックはリセットされますが、それ以降 < 時間がきたら > 内のプログラムは動作することはありません。`,
                                images: [
                                    {
                                        imageName: 'cb32ed8d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '07',
        connectorType: 'webSwitch',
        codeBlockOverView: {
            blockName: 'Webスイッチブロック',
            description: 'Webブラウザからブルーボードを操作するプログラムです。',
            detail_1: 'Webブラウザから、 アクションブロックの操作を行うことができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'Web ブロックの使い方',
                description:
                    'Web ブロックを使う上で、 ブルーボードと Web サイトを開くデバイス (PC やスマホ ) は同じ Wi-Fi につながっている必要があります。',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: 'c3182a1f.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'Wi-Fiにつなぐブロックの下にWebブロックをつなげてください',
                        examples: [
                            {
                                description: '以上で Web ブロックを使用する準備は完了です。',
                                images: [
                                    {
                                        imageName: '7c9277a0.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '遠隔で LED の ON、 OFF を操作しよう',
                description: 'Web サイトでブルーボードに接続された LED を光らせることができるプログラムです。',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: '< タイトル >、 < ラベル > を変更すると、 Web サイト上での表示も変更されます',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'e1d8695d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle:
                            'シリアルモニターに以下のようなリンクが表示されます。コピーしたものをブラウザの検索窓にペーストしてください',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '2497508b.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 3,
                        stepTitle: '検索すると以下のようなページが開かれます',
                        examples: [
                            {
                                description:
                                    '光るボタンをクリックすると LED が光り、 消えるボタンをクリックすると LED は光らなくなります。',
                                images: [
                                    {
                                        imageName: '9b742ee0.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '08',
        connectorType: 'webMonitor',
        codeBlockOverView: {
            blockName: 'Webモニターブロック',
            description: 'センサーが取得した数値を、 Webブラウザ上で確認するプログラムです。',
            detail_1: 'センサーが取得した数値を、 Webブラウザ上で確認することができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'Web ブロックの使い方',
                description:
                    'Web ブロックを使う上で、 ブルーボードと Web サイトを開くデバイス (PC やスマホ ) は同じ Wi-Fi につながっている必要があります。',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: '1d05d480.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'Wi-Fiにつなぐブロックの下にWebブロックをつなげてください',
                        examples: [
                            {
                                description: '以上で Web ブロックを使用する準備は完了です。',
                                images: [
                                    {
                                        imageName: '5d7f990d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '遠隔でセンサーの数値を表示しよう',
                description: 'Web サイトでブルーボードに接続されたセンサーの数値を確認することができるプログラムです。',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: '< センサー 1> の右側に測りたいセンサーブロックをつなげます',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '413e27d1.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle:
                            'シリアルモニターに以下のようなリンクが表示されます。コピーしたものをブラウザの検索窓にペーストしてください',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'b78c79dc.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 3,
                        stepTitle: '検索すると以下のようなページが開かれます',
                        examples: [
                            {
                                description: 'つなげたセンサーブロックの数値が Web サイト上に表示されます',
                                images: [
                                    {
                                        imageName: '4afa91eb.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'Web サイトでの表示を変更しよう',
                steps: [
                    {
                        examples: [
                            {
                                description:
                                    'Web ブロックの < タイトル > や < 単位 > を変更すると、 Web サイト上での表示も変更されます。',
                                images: [
                                    {
                                        imageName: '13175acd.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                    {
                                        imageName: '3398ab6c.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'センサーの数値を 2 つ表示しよう',
                description:
                    'Web サイトで、 ブルーボードに接続された磁力センサーと土じょう水分センサーの 2 つのセンサーの数値を同時に確認することができるプログラムです。',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: '< センサー 1> に土じょう水分センサーを、 < センサー 2> に磁力センサーをつなげる',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '5d825485.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle:
                            'Web サイトを更新すると、以下のように土じょう水分センサーと磁力センサーの数値が表示されます',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'a1f60eda.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'センサーの数値の更新される間隔を変更しよう',
                description: `Web サイトでセンサーの数値が更新されるタイミングを変更することができます。
                    < 10 秒 > のプルダウンを選択すると、 センサーの数値を自動更新する秒数を変更することができます。
                    < 手動更新 > をクリックすると、 クリックした瞬間のセンサーの数値を表示させることができます。`,
                steps: [
                    {
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '4100ea49.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '09',
        connectorType: 'slack',
        codeBlockOverView: {
            blockName: 'Slack ブロック',
            description: 'Slack にメッセージを送信するプログラムです。',
            detail_1: 'Slack にメッセージを送信することができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'Slack ブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: `Slack ブロックを使う前に、 Slackアプリ の設定が必要です。
                                アプリの設定手順につきましては、別途お問い合わせフォームよりご連絡ください。`,
                            },
                        ],
                        button: {
                            title: 'お問い合わせフォームはこちら',
                            url: `${NEXT_PUBLIC_BASE_URL}/inquiry/form/support?category_cd=05`,
                            backgroundColor: '#F4BB1B',
                        },
                    },
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: '5a2f3318.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'パラメーターに Slack tokenを入力してください',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '1d72a3b7.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 3,
                        stepTitle: 'Wi-Fi につなぐブロックの下に Slackブロックをつなげてください',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '57c1d7b5.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 4,
                        stepTitle: 'チャンネルを入力してください',
                        examples: [
                            {
                                description: '以上で Slack ブロックを使用する準備は完了です。',
                                images: [
                                    {
                                        imageName: '29795070.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'Slack ブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: 'メンションブロックを使用することでメンションを付ける事が出来ます。',
                                images: [
                                    {
                                        imageName: '0bc78e28.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 1,
                        stepTitle:
                            'メンションをつけるにはあらかじめユーザー ID を取得してメンションブロックに入力します。',
                        examples: [
                            {
                                description:
                                    'メンバーのプロフィール画面から「メンバー ID をコピー」をクリックすることで、 ユーザー ID がコピーされます。',
                                images: [
                                    {
                                        imageName: 'bb1c281a.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'コピーしたユーザー ID をメンションブロックに貼り付けます。',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'ef2b8762.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                    {
                                        imageName: '5b7f0409.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'Slack で土の水分量を知ることが出来るようにしよう',
                description:
                    'Wi-Fi につなぐブロックのSSID、 パスワード、 Slack token には各々の文字列をセットしてください。',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle:
                            '下はブルーボードに接続された土壌水分センサーが取得した値をSlack で確認することができるプログラムです',
                        examples: [
                            {
                                description:
                                    '土壌水分センサーが 50 の値を取得した場合、Slack には「現在の水分量は 50.00% です。」と送信されます。',
                                images: [
                                    {
                                        imageName: 'abc823f9.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: '2 プログラムを実行すると、Slack にメッセージが送信されます',
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'a57e38b8.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '10',
        connectorType: 'weatherGeo',
        codeBlockOverView: {
            blockName: '天気 API（緯度経度）ブロック',
            description: '緯度と経度を指定して、 その地点の天気データを取得するプログラムです。',
            detail_1: '緯度と経度を指定して、 その地点の天気データを取得することができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: '天気 API（緯度経度）ブロックの使い方',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: 'e3559d89.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle:
                            'Wi-Fiにつなぐブロックの下に数の変数ブロックと天気API（緯度経度）ブロックをつなげてください',
                        examples: [
                            {
                                description: `天気 API（緯度経度）ブロックを使用する場合、天気 API（緯度経度）ブロックの前に数の変数ブロックで変数 lat、 lon を入れる必要があります。
                                    また、 変数 lat には天気を観測したい地点の緯度、変数 lon には経度を入れてください。
                                    以上で天気 API（都市名）ブロックを使用する準備は完了です。`,
                                imageDescription: `天気 API（緯度経度）ブロックでは、 入力された緯度経度に近い気象観測所の天気情報を取得します。
                                    そのため、 入力された緯度経度地点上空の天気と異なる場合があります。`,
                                images: [
                                    {
                                        imageName: 'fef95c49.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '天気 ID について',
                steps: [
                    {
                        examples: [
                            {
                                description: `天気 API（緯度経度）ブロックでは、 取得した天気情報によって天気 ID が変わります。
                                    天気と天気 ID の関係は以下の通りです。たとえば観測地が「晴れ」ならば、 天気 ID は「1.00」が出力されます。`,
                                images: [
                                    {
                                        imageName: '4169cd43.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '天気名マッピングブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: `天気名マッピングブロックは、 天気情報から天気 ID に変換することができます。
                                    たとえば、 天気名マッピングブロックの「晴れ」を選択すると、「1.00」が出力されます。
                                    このブロックと「もし～ならばブロック」を組み合わせることで、 天気によって動作を変更することができます。`,
                                images: [
                                    {
                                        imageName: '40e35d07.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'ある場所の今の天気をフルカラー LED の色でわかるようにしよう',
                steps: [
                    {
                        examples: [
                            {
                                description: `下のプログラムは甲府駅の天気をフルカラー LED の色で判別するプログラムです。
                                    変数 lat に甲府駅の緯度、 変数 lon に甲府駅の経度を入れており、変数 i には甲府駅の天気 ID を入れています。
                                    「もし～ならばブロック」では、 変数 i が晴れ（1.00）ならフルカラー LEDが赤色に、 変数 i が曇り（2.00）ならフルカラー LED が白色に、 変数i が雨（4.00）ならフルカラー LED が青色に光ります。
                                    ※緯度と経度は Google map などから取得することができます。`,
                                images: [
                                    {
                                        imageName: 'e8f6a69d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '現在地の天気をフルカラー LED の色でわかるようにしよう',
                steps: [
                    {
                        examples: [
                            {
                                description:
                                    '下のプログラムで GPS センサーを使って現在地の天気を取得することができます。',
                                images: [
                                    {
                                        imageName: 'dfb52fe4.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '11',
        connectorType: 'weatherCity',
        codeBlockOverView: {
            blockName: '天気 API（都市名）ブロック',
            description: '都道府県名を指定して、 その地域の天気データを取得するプログラムです。',
            detail_1: '都道府県名を指定して、 その地域の天気データを取得することができます。',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: '天気 API（都市名）ブロックの使い方',
                steps: [
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: '2d1a9137.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'Wi-Fiにつなぐブロックの下に天気 API（都市名）ブロックをつなげてください',
                        examples: [
                            {
                                description: '以上で天気 API（都市名）ブロックを使用する準備は完了です。',
                                imageDescription: `天気 API（都市名）ブロックでは、 [ 観測地 ] で設定した都道府県の主要気象観測所の天気から取得します。
                                    そのため、 都道府県の主要気象観測所から距離が離れている地点では正確な天気を取得することができません。
                                    より観測地に近い気象観測所の天気を取得したい場合は、天気 API（緯度経度）ブロックをご使用ください。`,
                                images: [
                                    {
                                        imageName: '96e9afb2.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '天気 ID について',
                steps: [
                    {
                        examples: [
                            {
                                description: `天気 API（都市名）ブロックでは、 取得した天気情報によって天気 ID が変わります。
                                    天気と天気 ID の関係は以下の通りです。たとえば観測地が「晴れ」ならば、 天気 ID は「1.00」が出力されます。`,
                                images: [
                                    {
                                        imageName: '09a3655b.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: '天気名マッピングブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: `天気名マッピングブロックは、 上で示した天気一覧を選択することができます。
                                    選択すると、 天気名マッピングブロックに天気 ID が入ります。
                                    たとえば、 天気名マッピングブロックを「雨」を選択すると、 天気名マッピングブロックには「4.00」が入ります。
                                    下のプログラムのように、 天気 API（都市名）ブロックの値を取得した変数と天気名マッピングブロックをもしならばブロックで比較することで、「天気が雨のとき、 フルカラー LED を青色にする」ことができます。`,
                                images: [
                                    {
                                        imageName: 'd5d33466.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'ある都市の今の天気をフルカラー LED の色でわかるようにしよう',
                steps: [
                    {
                        examples: [
                            {
                                description: `下のプログラムは、天気 API（都市名）ブロックの [ 観測地 ] を特定の都道府県に変更することで、変数 i にその都道府県の天気 ID を入れることができます。
                                    もしならブロックでは、 変数 i が晴れ（1.00）ならフルカラー LED が赤色に、 変数 i が曇り（2.00）ならフルカラー LED が白色に、 変数 i が雨（4.00）ならフルカラー LED が青色に光ります。`,
                                images: [
                                    {
                                        imageName: 'f179b47b.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        contentsCd: '12',
        connectorType: 'lineMessaging',
        codeBlockOverView: {
            blockName: 'LINE 通知APIブロック',
            description: 'LINE にメッセージを送信するプログラムです。',
            detail_1: 'LINE にメッセージを送信することができます',
            codeBlockType: categoryBlockType.api,
            isBlueboard: true,
            isOrangeboard: false,
        },
        useCases: [
            {
                title: 'LINEブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: `LINE ブロックを使う前に、 LINE Messaging API の設定が必要です。
                                アプリの設定手順につきましては、別途お問い合わせフォームよりご連絡ください。`,
                            },
                        ],
                        button: {
                            title: 'お問い合わせフォームはこちら',
                            url: `${NEXT_PUBLIC_BASE_URL}/inquiry/form/support?category_cd=05`,
                            backgroundColor: '#F4BB1B',
                        },
                    },
                    {
                        stepNumber: 1,
                        stepTitle: 'Wi-Fi につなぐブロックのSSID、 パスワードを入力してください',
                        examples: [
                            {
                                description: `SSID とパスワードは、 Wi-Fiルータに記載されている情報を入力してください。
                                    メールアドレスは、 利用者登録時のメールアドレスを入力してください。
                                    ※SSID、 パスワード、 メールアドレスは1度書き込むとボードに記憶されるので、 2度目からは*印のままで書き込んでください。`,
                                images: [
                                    {
                                        imageName: 'cb32ed8d.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 2,
                        stepTitle: 'パラメーターに LINEトークンとユーザーID を入力してください',
                        examples: [
                            {
                                description: `LINE トークンには「LINE Messaging API の設定」を行い発行された「チャネルアクセストークン」を入力してください。
                                    ユーザーIDには「LINE Messaging APIの設定」を行い発行されたユーザーIDを入力してください。`,
                                images: [
                                    {
                                        imageName: '8fe6f8cd.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        stepNumber: 3,
                        stepTitle: 'Wi-Fi ブロックの下にLINE ブロックをつなげてください',
                        examples: [
                            {
                                imageDescription: `以上でLINEブロックを使用する準備は完了です。
                                    ※LINE公式アカウントのコミュニケーションプラン（無料）をご使用の場合は送ることが出来るメッセージが200通/月になります。
                                    ※通知間隔はデフォルトで1時間になっています。
                                    LINE ブロック実行時のHTTPステータスコードの内容は以下のリンクから確認できます。
                                    https://hello.schoomy.com/api-status-codes/`,
                                images: [
                                    {
                                        imageName: '394266a0.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'LINEで部屋の明るさを知ることができるようにしよう',
                steps: [
                    {
                        examples: [
                            {
                                description: `Wi-Fi につなぐブロックのSSID、 パスワード、 LINEトークン、 ユーザーIDには各々の文字列をセットしてください。
                                    下のプログラムでは、 ブルーボードに接続された明るさセンサーが取得した値をLINEで確認することができます。
                                    LINE には「 現在の部屋の明るさは50LUXです。」と送信されます。`,
                                images: [
                                    {
                                        imageName: '3655865b.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        examples: [
                            {
                                description: `プログラムを実行すると、 LINEにメッセージが送信されます。`,
                                images: [
                                    {
                                        imageName: 'ddc8ab79.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'LINE専用もし～ならブロックの使い方',
                steps: [
                    {
                        examples: [
                            {
                                description: `LINE メッセージを条件によって変更したい場合に使用します。`,
                                images: [
                                    {
                                        imageName: 'c2fae307.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'LINEでランダムなメッセージを送ろう',
                steps: [
                    {
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: '93262711.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        examples: [
                            {
                                description: `1 ～5のランダムな数字を変数iに代入して、 変数iの値に応じた「メッセージ1」～「メッセージ5」のメッセージがLINEに送られます。`,
                                images: [
                                    {
                                        imageName: '408d9a75.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
            {
                title: 'LINEブロック・専用もし～ならブロックで使用できるブロック',
                steps: [
                    {
                        examples: [
                            {
                                imageDescription: `LINE ブロックに使用できるブロック
                                    ・メッセージブロック
                                    ・LINE専用もし～ならブロック
                                    LINE 専用もし～ならブロックに使用できるブロック
                                    ・エリアブロック
                                    ・メッセージブロック`,
                                images: [
                                    {
                                        imageName: '50b0a4a9.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        examples: [
                            {
                                description: `※上記以外のブロックを使用すると以下のメッセージが表示されます。`,
                                images: [
                                    {
                                        imageName: 't2mu4vqy.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        examples: [
                            {
                                images: [
                                    {
                                        imageName: 'f542s3ik.png',
                                        baseWidth: 260,
                                        lgWidth: 520,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
];

export const BREADCRUMB_API_GUIDE_ITEMS: Record<string, BreadcrumbItem[]> = {
    [BASE_URL]: [{ label: `${CONTENTSET_TITLE} 目次` }],
    ...Object.fromEntries(
        BLUE_BOARD_GUIDE_DATA.map((item) => [
            `${BASE_URL}/${item.connectorType}`,
            [{ label: CONTENTSET_TITLE, href: BASE_URL }, { label: item.codeBlockOverView.blockName }],
        ]),
    ),
};
